/**
 * Card Variant Picker - Shop This Drop
 * Handles inline variant selection on product cards
 */

class CardVariantPicker {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.initializeAllCards();
    console.log('Card Variant Picker initialized');
  }

  bindEvents() {
    // Handle variant selection changes
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('card-variant-select')) {
        this.handleVariantChange(e.target);
      }
    });

    // Handle add to cart button clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('.card-add-to-cart-btn')) {
        e.preventDefault();
        this.handleAddToCart(e.target.closest('.card-add-to-cart-btn'));
      }
    });

    // Re-initialize when new content is loaded
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => this.initializeAllCards(), 100);
    });
  }

  initializeAllCards() {
    const cards = document.querySelectorAll('.card-variant-selectors');
    cards.forEach(card => this.initializeCard(card));
  }

  async initializeCard(cardElement) {
    const productId = cardElement.dataset.productId;
    if (!productId) return;

    try {
      // Fetch product data from Shopify
      const response = await fetch(`/products/${this.getProductHandle(productId)}.js`);
      if (!response.ok) return;
      
      const product = await response.json();
      this.updateVariantForCard(cardElement, product);
    } catch (error) {
      console.error('Error initializing card variant picker:', error);
    }
  }

  getProductHandle(productId) {
    // Try to get handle from card wrapper
    const cardWrapper = document.querySelector(`[data-product-id="${productId}"]`);
    return cardWrapper?.dataset.productHandle || productId;
  }

  handleVariantChange(selectElement) {
    const cardElement = selectElement.closest('.card-variant-selectors');
    const productId = cardElement.dataset.productId;
    
    this.updateVariantSelection(cardElement, productId);
  }

  async updateVariantSelection(cardElement, productId) {
    try {
      const productHandle = this.getProductHandle(productId);
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) return;
      
      const product = await response.json();
      const selectedOptions = this.getSelectedOptions(cardElement);
      const selectedVariant = this.findVariantByOptions(product.variants, selectedOptions);
      
      if (selectedVariant) {
        this.updateCardForVariant(cardElement, selectedVariant, product);
      }
    } catch (error) {
      console.error('Error updating variant selection:', error);
    }
  }

  getSelectedOptions(cardElement) {
    const selects = cardElement.querySelectorAll('.card-variant-select');
    const options = [];
    
    selects.forEach(select => {
      options.push(select.value);
    });
    
    return options;
  }

  findVariantByOptions(variants, selectedOptions) {
    return variants.find(variant => {
      return variant.options.every((option, index) => {
        return option === selectedOptions[index];
      });
    });
  }

  updateCardForVariant(cardElement, variant, product) {
    // Update hidden variant ID input
    const variantInput = cardElement.querySelector('.card-variant-id');
    if (variantInput) {
      variantInput.value = variant.id;
    }

    // Update add to cart button
    const addButton = cardElement.parentElement.querySelector('.card-add-to-cart-btn');
    if (addButton) {
      addButton.dataset.variantId = variant.id;
      
      const buttonText = addButton.querySelector('.card-add-to-cart-text');
      if (buttonText) {
        if (variant.available) {
          buttonText.textContent = 'Add to cart';
          addButton.disabled = false;
          addButton.classList.remove('sold-out');
        } else {
          buttonText.textContent = 'Sold out';
          addButton.disabled = true;
          addButton.classList.add('sold-out');
        }
      }
    }

    // Update price if price element exists
    this.updatePriceForVariant(cardElement, variant);

    // Trigger custom event for other scripts (like Shop This Drop)
    document.dispatchEvent(new CustomEvent('card:variant:change', {
      detail: {
        productId: product.id,
        variant: variant,
        product: product,
        cardElement: cardElement
      }
    }));
  }

  updatePriceForVariant(cardElement, variant) {
    const cardWrapper = cardElement.closest('.card-wrapper');
    const priceElement = cardWrapper?.querySelector('.price');
    
    if (priceElement && variant.price !== undefined) {
      const currentPrice = priceElement.querySelector('.price__current');
      const comparePrice = priceElement.querySelector('.price__compare');
      
      if (currentPrice) {
        currentPrice.textContent = this.formatMoney(variant.price);
      }
      
      if (comparePrice) {
        if (variant.compare_at_price > variant.price) {
          comparePrice.textContent = this.formatMoney(variant.compare_at_price);
          comparePrice.style.display = '';
        } else {
          comparePrice.style.display = 'none';
        }
      }
    }
  }

  formatMoney(cents) {
    // Basic money formatting - can be enhanced based on shop currency settings
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  async handleAddToCart(button) {
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    
    if (!variantId) {
      console.error('No variant ID found for add to cart');
      return;
    }

    // Show loading state
    button.classList.add('loading');
    button.disabled = true;

    try {
      // Use Shop This Drop's add to cart method if available
      if (window.shopThisDrop && window.shopThisDrop.addToCartViaAPI) {
        const productCard = button.closest('.card-wrapper');
        await window.shopThisDrop.addToCartViaAPI(variantId, productCard);
      } else {
        // Fallback to direct cart API
        await this.addToCartFallback(variantId);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError('Error adding product to cart');
    } finally {
      // Remove loading state
      button.classList.remove('loading');
      button.disabled = false;
    }
  }

  async addToCartFallback(variantId) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1
        }]
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add to cart');
    }

    // Trigger cart update event
    document.dispatchEvent(new CustomEvent('cart:updated'));
    
    return response.json();
  }

  showError(message) {
    // Simple error notification - can be enhanced
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ef4444;
      color: white;
      padding: 12px 16px;
      border-radius: 6px;
      z-index: 1000;
      font-size: 14px;
    `;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    setTimeout(() => notification.remove(), 3000);
  }

  updateVariantForCard(cardElement, product) {
    // This method ensures the card is initialized with the correct variant
    const firstVariant = product.variants[0];
    if (firstVariant) {
      this.updateCardForVariant(cardElement, firstVariant, product);
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.cardVariantPicker = new CardVariantPicker();
});

// Re-initialize on window load as backup
window.addEventListener('load', () => {
  if (!window.cardVariantPicker) {
    window.cardVariantPicker = new CardVariantPicker();
  }
});

// Export for use in other scripts
window.CardVariantPicker = CardVariantPicker;
